import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import { useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import ReactTextAreaAutosize from "react-textarea-autosize";

import { t } from "@/i18n-js/instance";

interface TitleProps {
  autoFocus?: boolean;
  onFocusChange?: (focused: boolean) => void;
  placeholder?: string;
}

export const Title = ({
  autoFocus = true,
  onFocusChange,
  placeholder,
}: TitleProps = {}) => {
  const { control } = useFormContext<PostFormValues>();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const labelText = placeholder ?? t("quick_post_v3.title_placeholder");

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  return (
    <Controller
      control={control}
      name="name"
      render={({ field }) => (
        <ReactTextAreaAutosize
          {...field}
          ref={element => {
            field.ref(element);
            ref.current = element;
          }}
          value={field.value ?? ""}
          maxLength={255}
          draggable={false}
          placeholder={labelText}
          className="text-primary placeholder:text-tertiary mb-5 block w-full resize-none border-none bg-primary p-0 text-heading-xl focus:ring-0"
          rows={1}
          onChange={event => field.onChange(event.target.value)}
          onFocus={() => onFocusChange?.(true)}
          onBlur={() => {
            field.onBlur();
            onFocusChange?.(false);
          }}
          onKeyDown={event => {
            if (event.key === "Enter") event.preventDefault();
          }}
          tabIndex={0}
          data-testid="post_name"
          aria-label={labelText}
        />
      )}
    />
  );
};
